import { useEffect, useState } from "react";
import { T } from "@/components/T";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { fetchWhatsNew } from "@/lib/api";
import KnowledgeCard from "@/components/KnowledgeCard";
import Seo from "@/components/Seo";
import { MandalaMotif } from "@/components/MotifBackground";
import { CalendarDays, Moon, BookOpen, Sparkles, Megaphone, Star, ArrowUpRight, ChevronRight } from "lucide-react";

const SECTIONS = [
  { key: "festivals", icon: CalendarDays, deva: "आगामी पर्व", en: "Upcoming Festivals", desc_en: "Major festivals ahead, with dates." },
  { key: "vrats", icon: Moon, deva: "व्रत", en: "Vrats", desc_en: "Ekadashi, Pradosh, weekly vrats & more." },
  { key: "stories", icon: BookOpen, deva: "कथाएँ", en: "Stories", desc_en: "The newest kathas added." },
  { key: "new", icon: Sparkles, deva: "नई सामग्री", en: "New Content", desc_en: "Fresh across the whole library." },
  { key: "updates", icon: Megaphone, deva: "अपडेट", en: "Updates", desc_en: "Announcements from Dharmartha." },
  { key: "custom", icon: Star, deva: "विशेष", en: "Custom", desc_en: "Curated notices & information." },
];

export default function WhatsNew() {
  const { section } = useParams();
  const cfg = SECTIONS.find((s) => s.key === section);
  if (section && cfg) return <SectionPage cfg={cfg} />;
  return <IndexPage />;
}

function IndexPage() {
  const { pick } = useLang();
  return (
    <div data-testid="whats-new-index">
      <Seo path="/whats-new" title="What's New — Dharmartha" description="Upcoming festivals, vrats, new stories and updates." />
      <section className="relative overflow-hidden border-b border-line bg-[var(--parchment)]">
        <MandalaMotif className="pointer-events-none absolute -right-24 -top-16 hidden h-[460px] w-[460px] text-[var(--gold)] opacity-[0.07] md:block" />
        <div className="relative z-[2] mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <p className="overline-label text-gold">{pick("नवीनतम", "What's New")}</p>
          <h1 className="mt-3 font-deva text-4xl leading-tight text-ink deva-lh md:text-6xl">नवीनतम</h1>
          <p className="mt-3 font-serif-latin text-xl text-ink-2 md:text-2xl"><T>Festivals, vrats, stories & updates</T></p>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SECTIONS.map((s, i) => (
            <motion.div key={s.key} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}>
              <Link to={`/whats-new/${s.key}`} data-testid={`whatsnew-card-${s.key}`} className="group flex h-full flex-col border border-line bg-surface p-6 transition-all hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-lg">
                <span className="flex h-11 w-11 items-center justify-center border border-[var(--gold-muted)] text-gold"><s.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-serif-latin text-xl text-ink">{pick(s.deva, s.en)}</h3>
                <p className="mt-2 flex-1 text-sm text-ink-2">{s.desc_en}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-saffron opacity-0 transition-opacity group-hover:opacity-100">{pick("देखें", "View")} <ArrowUpRight className="h-3.5 w-3.5" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}

function SectionPage({ cfg }) {
  const { pick } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); fetchWhatsNew(cfg.key).then(setData).catch(() => setData({ items: [] })); }, [cfg.key]);
  const items = data?.items || [];
  const recurring = data?.recurring || [];
  const isContent = cfg.key === "stories" || cfg.key === "new";

  return (
    <div data-testid={`whats-new-${cfg.key}`}>
      <Seo path={`/whats-new/${cfg.key}`} title={`${cfg.en} — Dharmartha`} description={cfg.desc_en} />
      <section className="relative overflow-hidden border-b border-line bg-[var(--parchment)]">
        <div className="relative z-[2] mx-auto max-w-7xl px-5 py-12 md:px-8 md:py-16">
          <nav className="flex items-center gap-1.5 text-xs text-ink-3">
            <Link to="/whats-new" className="hover:text-saffron">{pick("नवीनतम", "What's New")}</Link>
            <ChevronRight className="h-3 w-3" /><span className="text-ink-2">{pick(cfg.deva, cfg.en)}</span>
          </nav>
          <p className="mt-5 overline-label text-gold">{pick("नवीनतम", "What's New")}</p>
          <h1 className="mt-2 font-deva text-4xl leading-tight text-ink deva-lh md:text-5xl">{cfg.deva}</h1>
          <p className="mt-2 font-serif-latin text-xl text-ink-2">{cfg.en}</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 md:px-8 md:py-16">
        {!data ? <p className="py-16 text-center text-ink-3">…</p> : (
          <>
            {isContent && (
              items.length ? <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{items.map((it) => <KnowledgeCard key={it.slug} item={it} showImage />)}</div>
                : <p className="py-16 text-center text-ink-3">{pick("शीघ्र आ रहा है।", "Nothing new yet.")}</p>
            )}

            {(cfg.key === "festivals" || cfg.key === "vrats") && (
              <>
                {items.length ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {items.map((f) => (
                      <div key={f.id || f.slug} data-testid="whatsnew-festival" className="border border-line bg-surface p-5">
                        <p className="text-xs font-semibold uppercase tracking-wide text-saffron">{f.date}</p>
                        <p className="mt-1 font-serif-latin text-lg text-ink">{f.english_title || f.title || f.name}</p>
                        {(f.description || f.note) && <p className="mt-1 line-clamp-2 text-sm text-ink-2">{f.description || f.note}</p>}
                      </div>
                    ))}
                  </div>
                ) : <p className="py-10 text-center text-ink-3">{pick("शीघ्र आ रहा है।", "Nothing scheduled yet.")}</p>}
                {cfg.key === "vrats" && recurring.length > 0 && (
                  <div className="mt-10">
                    <h2 className="mb-4 font-serif-latin text-xl text-ink">{pick("नियमित व्रत", "Recurring Vrats")}</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {recurring.map((v, i) => (
                        <div key={i} className="border border-line bg-surface p-5">
                          <p className="font-serif-latin text-lg text-ink">{v.name || v.title || v.en}</p>
                          <p className="mt-1 text-sm text-ink-2">{v.day || v.when || v.note || v.deity || ""}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {(cfg.key === "updates" || cfg.key === "custom") && (
              items.length ? (
                <div className="grid gap-5 sm:grid-cols-2">
                  {items.map((a) => (
                    <div key={a.id} data-testid="whatsnew-update" className="flex gap-4 border border-line bg-surface p-5">
                      {a.image_url && <img src={a.image_url} alt="" className="h-16 w-16 shrink-0 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                      <div className="min-w-0">
                        <p className="font-serif-latin text-lg text-ink">{a.title || a.name}</p>
                        {(a.body || a.description) && <p className="mt-1 text-sm text-ink-2">{a.body || a.description}</p>}
                        {a.link && <Link to={a.link} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-saffron">{pick("और देखें", "Learn more")} <ArrowUpRight className="h-3.5 w-3.5" /></Link>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="py-16 text-center text-ink-3">{pick("अभी कुछ नहीं।", "Nothing here yet.")}</p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
